import { Injectable } from '@angular/core';
import { URL_SERVICIOS, HTTP_OPTIONS } from '../shared/config/config';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { EncryptDecryptService } from './encrypt-decrypt.service';
import {
    AuthorizeInvoice,
    DocumentPayment,
    Petition,
    ValidationDocument,
    DocumentVersionValidationInvoice,
    DocumentVersion,
    DocumentNewVersion,
    DocumentVersionValidationEnrollment,
    DatatableRootObject,
    DataTablesResponse
 } from '../interfaces/interfaces.index';
import { messageValidation } from '../interfaces/messageValidation.interface';
import { BillingLetter, DocumentsRecepeit } from '../interfaces/payment-document-info.interface';
import { Base64Obj, DocumentEnrollmentVersion } from '../interfaces/document-validation';
import { Document, BaseDocument, DocumentActivity } from 'src/app/interfaces/interfaces.index'
const URL = `${URL_SERVICIOS}Document`;


@Injectable({
    providedIn: 'root'
})

export class DocumentService {

    constructor(
        private http: HttpClient,
        private encryptDecryptService: EncryptDecryptService
    ) { }

    private normalizeNumericId(id: number | string): number | string {
        if (environment.encryption || id === null || id === undefined) {
            return id;
        }

        if (typeof id === 'number') {
            return id;
        }

        if (/^\d+$/.test(id)) {
            return Number(id);
        }

        try {
            // Handle URL-safe base64 token: '-' -> '+', '_' -> '/', plus padding.
            let token = id.replace(/-/g, '+').replace(/_/g, '/');
            while (token.length % 4) {
                token += '=';
            }

            const decoded = atob(token);
            const decrypted = this.encryptDecryptService.decryptUsingAES256(decoded);
            if (/^\d+$/.test(decrypted)) {
                return Number(decrypted);
            }
        } catch (error) {
            // Keep original value if normalization fails.
        }

        return id;
    }

    SendAuthorizeInvoice = (data: AuthorizeInvoice) =>
        this.http.post<AuthorizeInvoice>(`${URL}/authorizeInvoice`, JSON.stringify(data), HTTP_OPTIONS);

    SendAuthorizeInfo = (authorize: AuthorizeInvoice) =>
        this.http.put<AuthorizeInvoice>(`${URL}/authorizeInvoiceUpdate?authorizeInvoiceId=${authorize.id}`, JSON.stringify(authorize), HTTP_OPTIONS);

    GetInvoiceByPetition = (data: Petition) =>
        this.http.get<DocumentPayment>(`${URL}/Petition/${data.id}`, HTTP_OPTIONS);

    GetPagareByPetitionAndName = (petitionId: number, name: string) =>
        this.http.get<any>(`${URL}/petition/${petitionId}/documentName/${name}`, HTTP_OPTIONS);

    GetValidationInfo = (petitionId: number) =>
        this.http.get<any>(`${URL}/authorizeInvoice/${petitionId}`, HTTP_OPTIONS);

    getPagare = (id: number, documento: string, userId = 0, lastVersion = false) =>
        this.http.get<any>(`${URL}/petition/${id}/documentName/${documento}?userId=${userId}&lastVersion=${lastVersion}`, HTTP_OPTIONS);

    SendValidationInfo = (data: ValidationDocument[]) =>
        this.http.post<ValidationDocument[]>(`${URL}/validationDocument?AuthorizeinvoiceId=${data[0].authorizeInvoiceId}`, JSON.stringify(data), HTTP_OPTIONS);

    postBillingLetter = (id: number, userId: number) =>
        this.http.post<BillingLetter>(`${URL}/Billingletter/${id}/userLogin/${userId}`,  HTTP_OPTIONS);

    postEnrollmentLetter = (id: number, petitionId: number) =>
        this.http.post<messageValidation>(`${URL}/CreateEnrollmentLetterPDF?petitionId=${petitionId}&userId=${id}`,  HTTP_OPTIONS);

    getAuthorizeInvoice = (petitionId: number) =>
        this.http.get<AuthorizeInvoice>(`${URL}/authorizeInvoice/${petitionId}`, HTTP_OPTIONS);

    getDocumentsReceipt = (petitionId: number) =>
        this.http.get<DocumentsRecepeit>(`${URL}/DatesReceiptDocuments/${petitionId}`, HTTP_OPTIONS)

    getAllDocuments = (petitionId: number, paymentCapacityId: number) =>
        this.http.get<DocumentPayment[]>(`${URL}/petition/${petitionId}/paymentCapacity/${paymentCapacityId}`, HTTP_OPTIONS)

    getAllDocumentsByPetition = (petitionId: number | string, validateType: string, userId: number | string = 0) => {
        const normalizedPetitionId = this.normalizeNumericId(petitionId);
        const normalizedUserId = this.normalizeNumericId(userId);
        return this.http.get<Document[]>(`${URL}/all/documents/petition/${normalizedPetitionId}/validateType/${validateType}/user/${normalizedUserId}`, HTTP_OPTIONS);
    }

    getAllDocumentsByUserId = (userId: number, validateType: string = null, isUser: boolean = true) =>
        this.http.get<Document[]>(`${URL}/all/documents/user/${userId}/validateType/${validateType}/${isUser}`, HTTP_OPTIONS);

    sendDocumentVersion = (document) =>
        this.http.post(`${URL}/DocumentVersion`, document, HTTP_OPTIONS);

    sendDocumentEnrollmentVersion = (document) =>
        this.http.post<DocumentVersionValidationEnrollment>(`${URL}/DocumentVersionEnrollment`, document, HTTP_OPTIONS);

    getDocumentsVersion = (documentId: number) =>
        this.http.get<DocumentVersion[]>(`${URL}/DocumentVersion/all/${documentId}`, HTTP_OPTIONS);

    getDocumentsEnrollmentVersion = (documentId: number) =>
        this.http.get(`${URL}/DocumentEnrollmentVersion/all/${documentId}`, HTTP_OPTIONS);

    statusDocumentVersion = authDocument =>
        this.http.put(`${URL}/DocumentVersion/all`, authDocument, HTTP_OPTIONS);

    changeDocumentStatus = authDocument =>
      this.http.put(`${URL}/ChangeDocumentStatus/all`, authDocument, HTTP_OPTIONS);

    statusDocumentEnrollmentVersion = authDocument =>
        this.http.put(`${URL}/DocumentEnrollmentVersion/all`, authDocument, HTTP_OPTIONS);

    statusDocument = (document, userId: number) =>
        this.http.put(`${URL}/Document/all?userId=${userId}`, document, HTTP_OPTIONS);

    statusDocumentEnrollment = document =>
        this.http.put(`${URL}/DocumentEnrollment/all`, document, HTTP_OPTIONS);

    getAllBaseDocument = () =>
        this.http.get<BaseDocument[]>(`${URL}/GetAllBaseDocument`, HTTP_OPTIONS);

    getDocumentsByActivity = (petitionId: number, userId: number, fase: number = 0) =>
        this.http.get<DocumentActivity[]>(`${URL}/GetDocumentsByActivity/${petitionId}/${userId}${fase ? '?fase=' + fase : ''}`, HTTP_OPTIONS);

    getDocumentsDataTable = (tableObj: DatatableRootObject) => this.http.post<DataTablesResponse<BaseDocument>>(`${URL}/datatable`, tableObj, HTTP_OPTIONS);

    getBaseDocument = (id: string) => this.http.get<BaseDocument>(`${URL}/GetBaseDocument/${id}`);

    createBaseDocument = (baseDocument: BaseDocument) => this.http.post<string>(URL, baseDocument, HTTP_OPTIONS);

    updateBaseDocument = (baseDocument: BaseDocument, id: string) => this.http.put<string>(`${URL}/UpdateBaseDocument/${id}`, baseDocument, HTTP_OPTIONS);

    deleteBaseDocumentById = (id: string) => this.http.delete(`${URL}/DeleteBaseDocument/${id}`);

    getDocumentBase64 = (id: number) => this.http.get<Base64Obj>(`${URL}/${id}`);

    getDocumentVersionBase64 = (id: number, documentType: string) => this.http.get<Base64Obj>(`${URL}/version/${id}?type=${documentType}`);
}
